import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { Add, Remove } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GET_DICTIONARIES } from "../graphql/getDictionaries";
import { GET_PEOPLE } from "../graphql/getPeople";
import { GET_WELD } from '../graphql/getWeld';
import { CREATE_OR_UPDATE_LOGS } from '../graphql/createOrUpdateLogs';

const InspectionLogForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('backUrl');
  const [projectId] = useState(Number(useParams().projectId));
  const [elementId] = useState(Number(useParams().elementId));

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00`;
  };

  const [inputs, setInputs] = useState({
    person: "",
    date: getTodayDate(),
    note: "",
    inspectionType: "",
    percentage: "",
    millimeters: "",
    weldFailed: "",
    doubleLength: false,
    gauging: false,
    bypass_hold_time: false,
    additionalParams: [
      { key: "", value: "" }
    ],
  });

  const { data: people, loading: queryLoading, error: queryError } = useQuery(GET_PEOPLE);
  const { data: dictionaries } = useQuery(GET_DICTIONARIES);

  const weldIdParam = useParams().weldId;
  const [weldId] = useState(weldIdParam);
  const [baseTotalPercentage, setBaseTotalPercentage] = useState(0)
  const [weldNo, setWeldNo] = useState(0)
  const [weld, setWeld] = useState({});
  const weldsArray = Array.isArray(weld) ? weld : (Object.keys(weld).length > 0 ? [weld] : []);
  const weldLoaded = weldsArray.length > 0;
  const inspections_type_ids = weldsArray.flatMap(w => w?.inspection_category?.inspections?.map(item => Number(item.type_id)) || []);
  const isMultipleWelds = String(weldId).includes(',');

  const peopleList = people?.people?.list?.map(person => ({
    id: person.id,
    name: person.name
  })) || [];

  const inspectionCategoryTypes = !weldLoaded ? [] : (isMultipleWelds
    ? dictionaries?.inspection_category_types
    : dictionaries?.inspection_category_types?.filter(item => inspections_type_ids.includes(Number(item.id)))
  )?.map(item => ({
    id: item.id,
    name: item.name
  })) || [];

  const riTypeId = dictionaries?.inspection_category_types?.find(o => o.name === 'RI')?.id;
  const isRiType = riTypeId ? inspectionCategoryTypes.find(t => t.name === inputs.inspectionType)?.id === riTypeId : false;

  const selectedTypeId = inspectionCategoryTypes.find(t => t.name === inputs.inspectionType)?.id;

  const getGroupDataForType = (typeId) => {
    if (!typeId || !weldsArray[0]) return null;
    const raw = weldsArray[0].inspection_groups;
    if (!raw) return null;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const entry = Object.entries(parsed).find(([, e]) => String(e.insp_cat_type_id) === String(typeId));
    return entry ? { groupId: entry[0], ...entry[1] } : null;
  };

  const groupData = selectedTypeId ? getGroupDataForType(selectedTypeId) : null;
  const isGroupRepresentative = groupData?.representative === true;
  const isBlockedByGroup = groupData !== null && groupData?.representative === false;

  const permissions = localStorage.permissions ? JSON.parse(localStorage.permissions) : {};
  const canBypassHoldTime = permissions?.wl_welds?.inspection_logs?.bypass_hold_time;

  const parseDateStr = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('.')) {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('.');
      return new Date(`${year}-${month}-${day}T${timePart || '00:00'}`);
    }
    return new Date(dateStr);
  };

  const getFirstWeldingDate = (w) => {
    const logs = w.logs || [];
    const dates = logs
      .filter(l => (l.type === 'WlWelds::Logs::Welding' || l.type === 'WlWelds::Logs::Grinding') && l.date)
      .map(l => parseDateStr(l.date))
      .filter(d => d && !isNaN(d));
    if (dates.length > 0) return new Date(Math.min(...dates));
    return parseDateStr(w.welder?.date);
  };

  const getLastWeldingDate = (w) => {
    const logs = w.logs || [];
    const dates = logs
      .filter(l => (l.type === 'WlWelds::Logs::Welding' || l.type === 'WlWelds::Logs::Grinding') && l.date)
      .map(l => parseDateStr(l.date))
      .filter(d => d && !isNaN(d));
    if (dates.length > 0) return new Date(Math.max(...dates));
    return parseDateStr(w.welder?.date);
  };

  const getBlockReason = () => {
    if (isBlockedByGroup) {
      return { type: 'group_blocked', message: 'This weld is covered by a group representative — inspection can only be logged for the representative weld.' };
    }
    const holdTime = weldsArray[0]?.inspection_category?.hold_time;
    for (const w of weldsArray) {
      if (!inputs.gauging) {
        const firstWeldingDate = getFirstWeldingDate(w);
        if (!firstWeldingDate) {
          return { type: 'no_welding', message: `Weld ${w.no}: only gauging inspections are allowed before the first welding or grinding.` };
        }
        if (inputs.date) {
          const inspDate = new Date(inputs.date);
          const firstDate = new Date(firstWeldingDate.getFullYear(), firstWeldingDate.getMonth(), firstWeldingDate.getDate());
          const inspDateOnly = new Date(inspDate.getFullYear(), inspDate.getMonth(), inspDate.getDate());
          if (inspDateOnly < firstDate) {
            return { type: 'no_welding', message: `Weld ${w.no}: only gauging inspections are allowed before the first welding or grinding.` };
          }
        }
      }
      if (!inputs.gauging && !isRiType && !inputs.bypass_hold_time && holdTime) {
        const lastWeldingDate = getLastWeldingDate(w);
        if (lastWeldingDate) {
          const holdUntil = new Date(lastWeldingDate.getTime() + holdTime * 60 * 60 * 1000);
          if (holdUntil > new Date()) {
            const pad = n => String(n).padStart(2, '0');
            const holdUntilStr = `${pad(holdUntil.getDate())}.${pad(holdUntil.getMonth()+1)}.${holdUntil.getFullYear()} ${pad(holdUntil.getHours())}:${pad(holdUntil.getMinutes())}`;
            return { type: 'hold_time', message: `Weld ${w.no}: hold time not met — inspection not allowed until ${holdTime}h after welding. Earliest: ${holdUntilStr}` };
          }
        }
      }
    }
    return null;
  };

  const inspectionLogDataTypes = dictionaries?.inspection_log_data_types?.map(item => ({
    id: item.id,
    name: item.name
  })) || [];

  const [inspectionSelected, setInspectionSelected] = useState('category');

  const getCategoryMm = () => {
    const pct = parseFloat(inputs.percentage);
    const len = Array.isArray(weld) ? weld[0]?.length : weld?.length;
    return pct && len ? Math.round(Number(len) * (pct / 100)) : 0;
  };

  const getCompleteMm = () => inputs.millimeters || 0;

  const getCompletePercent = () => {
    if (isMultipleWelds) return inspectionSelected === '100' ? '100' : 'as-per-insp-cat';
    const len = Array.isArray(weld) ? weld[0]?.length : weld?.length;
    return len ? String(Math.round((getCompleteMm() * 100) / Number(len))) : '0';
  };

  const handleInspectionButtonClick = (type) => {
    setInspectionSelected(type);
    if (!isMultipleWelds) {
      const len = Number(Array.isArray(weld) ? weld[0]?.length : weld?.length);
      const pct = type === '100' ? 100 : parseFloat(inputs.percentage);
      const mm = pct && len ? Math.round(len * (pct / 100)) : 0;
      setInputs(prev => ({ ...prev, millimeters: mm }));
    }
  };
  const [createOrUpdateLogs] = useMutation(CREATE_OR_UPDATE_LOGS);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const handleChangeDateOrNote = (e) => {
    const { name, value } = e.target;
    setInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value
    }));
  };

  const handleInspectionTypeChange = (event) => {
    const selectedTypeId = inspectionCategoryTypes.find(type => type.name === event.target.value)?.id;
    let matchingInspection;

    if (Array.isArray(weld) && weld.length > 1) {
      // For multiple welds, check if all have same length and same inspection_category
      const allSameLengthAndCategory = weld.every(w =>
        w?.length === weld[0]?.length &&
        w?.inspection_category?.id === weld[0]?.inspection_category?.id
      );
      if (allSameLengthAndCategory) {
        matchingInspection = weld[0]?.inspection_category?.inspections?.find(inspection => inspection.type_id === selectedTypeId);
      }
    } else {
      matchingInspection = weld?.inspection_category?.inspections?.find(inspection => inspection.type_id === selectedTypeId);
    }

    setInputs(prev => ({
      ...prev,
      inspectionType: event.target.value,
      percentage: matchingInspection?.range || "100",
      doubleLength: matchingInspection?.double_length || false,
    }));
    setInspectionSelected('category');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddParam = () => {
    setInputs((prev) => ({
      ...prev,
      additionalParams: [...prev.additionalParams, { key: "", value: "" }]
    }));
  };

  const handleRemoveParam = (index) => {
    setInputs((prev) => ({
      ...prev,
      additionalParams: prev.additionalParams.filter((_, i) => i !== index)
    }));
  };

  const handleParamChange = (index, field, value) => {
    const updated = [...inputs.additionalParams];
    updated[index][field] = value;
    setInputs((prev) => ({ ...prev, additionalParams: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!inputs.person) errors.person = 'Person is required';
    if (!inputs.inspectionType) errors.inspectionType = 'Inspection Type is required';

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!isRiType && !isMultipleWelds && inputs.weldFailed && inputs.millimeters) {
      if (parseInt(inputs.weldFailed) > parseInt(inputs.millimeters)) {
        setValidationErrors({ weldFailed: `Weld failed (${inputs.weldFailed} mm) cannot exceed inspected length (${inputs.millimeters} mm)` });
        return;
      }
    }

    setIsSaving(true);
    try {
      const formattedDate = (() => {
        const [datePart, timePart] = inputs.date.split('T');
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year} ${timePart || '00:00'}`;
      })();

      await createOrUpdateLogs({
        variables: {
          completePercent: isRiType ? '100' : getCompletePercent(),
          complete: isRiType ? null : (isMultipleWelds ? 0 : (parseInt(inputs.millimeters) || 0)),
          log_data: JSON.stringify(inputs.additionalParams.reduce((acc, param) => {
            const typeId = inspectionLogDataTypes.find(type => type.name === param.key)?.id;
            if (typeId && param.value) {
              acc[typeId] = param.value;
            }
            return acc;
          }, {})),
          note: inputs.note,
          person_id: typeof inputs.person === 'string' && inputs.person.includes('-')
            ? inputs.person.split('-').pop()
            : inputs.person,
          type: 'WlWelds::Logs::Inspection',
          welds_id: Array.isArray(weldId) ? weldId.map(id => parseInt(id)) : String(weldId).split(',').map(id => parseInt(id.trim())),
          date: formattedDate,
          inspection_category_type_id: inspectionCategoryTypes.find(type => type.name === inputs.inspectionType)?.id,
          weld_failed: inputs.weldFailed && parseInt(inputs.weldFailed) ? parseInt(inputs.weldFailed) : null,
          double_length: inputs.doubleLength,
          gauging: inputs.gauging,
          skipped: false
        }
      });
      console.log('Log created successfully');
      if (backUrl) {
        window.location.href = atob(backUrl);
      } else {
        navigate(`/welds/${projectId}/${elementId}`);
      }
    } catch (error) {
      console.error('Error creating log:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipInspection = async () => {
    const errors = {};
    if (!inputs.inspectionType) errors.inspectionType = 'Inspection Type is required';

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (window.confirm('Are you sure?')) {
      setIsSaving(true);
      try {
        const formattedDate = (() => {
          const [datePart, timePart] = inputs.date.split('T');
          const [year, month, day] = datePart.split('-');
          return `${day}.${month}.${year} ${timePart || '00:00'}`;
        })();

        await createOrUpdateLogs({
          variables: {
            date: formattedDate,
            note: inputs.note,
            inspection_category_type_id: inspectionCategoryTypes.find(type => type.name === inputs.inspectionType)?.id,
            welds_id: Array.isArray(weldId) ? weldId.map(id => parseInt(id)) : String(weldId).split(',').map(id => parseInt(id.trim())),
            type: 'WlWelds::Logs::Inspection',
            double_length: false,
            gauging: false,
            log_data: JSON.stringify({}),
            completePercent: '0',
            complete: 0,
            weld_failed: null,
            skipped: true
          }
        });
        console.log('Inspection skipped successfully');
        if (backUrl) {
          window.location.href = atob(backUrl);
        } else {
          navigate(`/welds/${projectId}/${elementId}`);
        }
      } catch (error) {
        console.error('Error skipping inspection:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };
  const [getWeld] = useLazyQuery(GET_WELD);

  useEffect(() => {
    const weldIds = Array.isArray(weldId) ? weldId : String(weldId).split(',').map(id => parseInt(id.trim()));

    Promise.all(weldIds.map(id =>
      getWeld({ variables: { weldId: id } })
    )).then((responses) => {
      const welds = responses.map(response => response.data?.weld).filter(Boolean);
      setWeld(welds.length === 1 ? welds[0] : welds);
      setBaseTotalPercentage(welds[0]?.welding_progress || 0);
      setWeldNo(welds[0]?.no || 0);
    })
  }, [weldId, getWeld])

  // When inspection type changes and weld is group representative, auto-set 100%
  useEffect(() => {
    if (!inputs.inspectionType || !selectedTypeId) return;
    const gd = getGroupDataForType(selectedTypeId);
    if (gd?.representative === true) {
      const len = Number(weldsArray[0]?.length);
      setInputs(prev => ({ ...prev, millimeters: len, percentage: '100' }));
      setInspectionSelected('100');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs.inspectionType, selectedTypeId]);

  useEffect(() => {
    const percentageNum = parseFloat(inputs.percentage);
    let weldLength;

    if (Array.isArray(weld) && weld.length > 1) {
      // Check if all welds have the same length and same inspection_category
      const allSameLengthAndCategory = weld.every(w =>
        w?.length === weld[0]?.length &&
        w?.inspection_category?.id === weld[0]?.inspection_category?.id
      );
      if (allSameLengthAndCategory) {
        weldLength = weld[0]?.length;
      }
    } else {
      weldLength = Array.isArray(weld) ? weld[0]?.length : weld.length;
    }

    if (!isNaN(percentageNum) && weldLength) {
      const calculatedLength = weldLength * (percentageNum / 100);
      const flooredLength = Math.floor(calculatedLength * 10) / 10;
      const roundedLength = flooredLength === 0 ? 0.1 : flooredLength;
      setInputs(prev => ({ ...prev, millimeters: roundedLength }));
    } else {
      setInputs(prev => ({ ...prev, length: "" }));
    }
  }, [inputs.percentage, weld]);

  const renderWeldsAccordion = () => {
    const weldsArray = Array.isArray(weld) ? weld : [weld];

    return (
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Weld Information ({weldsArray.length} weld{weldsArray.length > 1 ? 's' : ''})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Weld Type</TableCell>
                  <TableCell>Length</TableCell>
                  <TableCell>WPS</TableCell>
                  <TableCell>Welding Progress</TableCell>
                  <TableCell>Inspection Category</TableCell>
                  <TableCell>Inspections</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weldsArray.map((weldItem, index) => (
                  <TableRow key={weldItem?.id || index}>
                    <TableCell>{weldItem?.no || '-'}</TableCell>
                    <TableCell>
                      {Array.isArray(weldItem?.weld_joint_type) && weldItem.weld_joint_type.length > 0 ?
                        weldItem.weld_joint_type.map((wjt, index) => {
                          if (wjt.weld_type?.name && wjt.joint_type?.name) {
                            return `${wjt.weld_type.name}-${wjt.joint_type.name}`;
                          } else if (wjt.weld_type?.name) {
                            return `${wjt.weld_type.name}`;
                          }
                          return null;
                        }).filter(Boolean).join(', ') : '-'
                      }
                    </TableCell>
                    <TableCell>{weldItem?.length || '-'}</TableCell>
                    <TableCell>
                      {Array.isArray(weldItem?.wps) && weldItem.wps.length > 0 ? 
                        weldItem.wps.map((wpsItem, index) => (
                          <div key={wpsItem.id}>
                            {wpsItem.no}
                          </div>
                        )) : '-'
                      }
                    </TableCell>
                    <TableCell>{weldItem?.welding_progress ? `${weldItem.welding_progress}%` : '-'}</TableCell>
                    <TableCell>
                      {weldItem?.inspection_category?.name ? (
                        <>
                          {weldItem.inspection_category.name}
                          <br />
                          ({[...weldItem.inspection_category.inspections || []].sort((a, b) => a.type_id - b.type_id).map(inspection => {
                            const typeName = dictionaries?.inspection_category_types?.find(type => type.id === inspection.type_id)?.name || inspection.name;
                            return `${typeName} - ${inspection.range}%`;
                          }).join(', ') || ''})
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {weldItem?.inspections ? (
                        Object.entries(typeof weldItem.inspections === 'string' ? JSON.parse(weldItem.inspections) : weldItem.inspections).map(([typeId, inspectionData]) => {
                          const typeName = dictionaries?.inspection_category_types?.find(type => type.id === parseInt(typeId))?.name || `ID:${typeId}`;
                          const color = inspectionData?.skipped ? 'orange' : (inspectionData?.positive ? 'green' : 'red');
                          const date = inspectionData?.date || 'No date';
                          return (
                            <div key={typeId} style={{ color }}>
                              {typeName} - "{date}"
                            </div>
                          );
                        })
                      ) : '-'}
                    </TableCell>
                    <TableCell>{weldItem?.status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Inspection Log
      </Typography>
      {(Array.isArray(weld) ? weld.length > 0 : Object.keys(weld).length > 0) && renderWeldsAccordion()}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
          <Autocomplete
            options={peopleList}
            getOptionLabel={(option) => option.name || ""}
            value={peopleList.find(p => `${p.name}-${p.id}` === inputs.person) || null}
            onChange={(event, newValue) => {
              const val = newValue ? `${newValue.name}-${newValue.id}` : "";
              setInputs(prev => ({ ...prev, person: val }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Person"
                required
                fullWidth
                error={!!validationErrors.person}
                helperText={validationErrors.person}
              />
            )}
            noOptionsText="No people available"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Inspection Type"
            name="inspectionType"
            value={inputs.inspectionType}
            onChange={handleInspectionTypeChange}
            fullWidth
            required
            error={!!validationErrors.inspectionType}
            helperText={validationErrors.inspectionType}
          >
            {inspectionCategoryTypes.map(type => {
              const gd = getGroupDataForType(type.id);
              const blocked = gd !== null && gd?.representative === false;
              return (
                <MenuItem key={type.id} value={type.name} disabled={blocked}>
                  {type.name}{blocked ? ` (group ${gd.groupId})` : ''}
                </MenuItem>
              );
            })}
          </TextField>
          {(() => {
            const raw = weldsArray[0]?.inspection_groups;
            if (!raw) return null;
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return Object.entries(parsed).map(([groupId, entry]) => {
              const typeName = inspectionCategoryTypes.find(t => String(t.id) === String(entry.insp_cat_type_id))?.name || `Type ${entry.insp_cat_type_id}`;
              if (entry.representative === true) {
                return (
                  <Typography key={groupId} variant="body2" sx={{ color: '#1976d2', mt: 0.5 }}>
                    ★ {typeName} — representative weld (group {groupId}), inspection will be applied to the entire group
                  </Typography>
                );
              } else {
                return (
                  <Typography key={groupId} variant="body2" sx={{ color: 'orange', mt: 0.5 }}>
                    {typeName} — weld is in group {groupId}, only the representative weld can be inspected
                  </Typography>
                );
              }
            });
          })()}
        </Grid>
        <Typography ml={2} gutterBottom>
          Length
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ px: 2 }}>
          {!isMultipleWelds && (
            <Grid item xs={12} sm={4}>
              <TextField
                label="Millimeters"
                name="millimeters"
                type="number"
                value={inputs.millimeters || ''}
                onChange={(e) => {
                  const mm = Number(e.target.value) || 0;
                  const len = Number(Array.isArray(weld) ? weld[0]?.length : weld?.length);
                  setInputs(prev => ({ ...prev, millimeters: mm }));
                  setInspectionSelected(mm === getCategoryMm() ? 'category' : mm === len ? '100' : 'custom');
                }}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={isMultipleWelds ? 12 : 8}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={inspectionSelected === 'category' ? 'contained' : 'outlined'}
                onClick={() => handleInspectionButtonClick('category')}
                sx={{ flex: 3 }}
              >
                As per insp. cat.
              </Button>
              <Button
                variant={inspectionSelected === '100' ? 'contained' : 'outlined'}
                onClick={() => handleInspectionButtonClick('100')}
                sx={{ flex: 1 }}
              >
                100%
              </Button>
            </Box>
          </Grid>
          {!isMultipleWelds && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {inputs.millimeters || 0} mm is <strong>{getCompletePercent()}%</strong> of weld length
              </Typography>
            </Grid>
          )}
        </Grid>
        {!isMultipleWelds && (<>
        <Grid container spacing={2} alignItems="center" sx={{ px: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inputs.doubleLength}
                  onChange={(e) => setInputs(prev => ({ ...prev, doubleLength: e.target.checked }))}
                />
              }
              label="Double Length"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center" sx={{ px: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inputs.gauging}
                  onChange={(e) => setInputs(prev => ({ ...prev, gauging: e.target.checked }))}
                />
              }
              label="Gauging"
            />
          </Grid>
          {canBypassHoldTime && !isRiType && (
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inputs.bypass_hold_time}
                    onChange={(e) => setInputs(prev => ({ ...prev, bypass_hold_time: e.target.checked }))}
                  />
                }
                label="Bypass hold time"
              />
            </Grid>
          )}
        </Grid>
        <Typography ml={2} gutterBottom>
          Weld Failed (millimeters)
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ px: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="Weld Failed"
              name="weldFailed"
              value={inputs.weldFailed}
              onChange={handleChange}
              fullWidth
              disabled={isMultipleWelds}
            />
            {isMultipleWelds && (
              <Typography sx={{ color: '#F00', fontSize: '13px', mt: 0.5 }}>
                Weld failed can be logged only for single weld
              </Typography>
            )}
          </Grid>
        </Grid>

        {inputs.additionalParams.map((param, index) => (
          <Grid container spacing={0} alignItems="center" key={index}>
            <Grid item xs={12} sm={4} mt={2} ml={2}>
              <TextField
                select
                label="Additional Parameter"
                value={param.key}
                onChange={(e) => handleParamChange(index, "key", e.target.value)}
                fullWidth
              >
                {(Array.isArray(weldId) || String(weldId).includes(',') ? inspectionLogDataTypes : inspectionLogDataTypes.filter(type =>
                  !inputs.additionalParams.some((p, i) => i !== index && p.key === type.name)
                )).map(type => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3} mt={2} ml={2}>
              <TextField
                label="Value"
                value={param.value}
                onChange={(e) =>
                  handleParamChange(index, "value", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <IconButton
                onClick={() => handleRemoveParam(index)}
                sx={{ mt: { xs: 0, sm: 1 } }}
              >
                <Remove />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button variant="outlined" onClick={handleAddParam} startIcon={<Add />}>
            Add Additional Parameter
          </Button>
        </Grid>
        </>)}
        <Grid item xs={12} sm={3}>
          <TextField
            type="datetime-local"
            label="Date"
            name="date"
            value={inputs.date}
            fullWidth
            required
            sx={{ marginTop: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleChangeDateOrNote}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            type="text"
            label="Notes"
            name="note"
            value={inputs.note}
            fullWidth
            multiline
            rows={4}
            sx={{ marginTop: 4 }}
            onChange={handleChangeDateOrNote}
          />
        </Grid>

        {(() => { const reason = getBlockReason(); return reason ? (
          <Grid item xs={12}>
            <Typography sx={{ color: 'red', fontWeight: 'bold', mt: 1 }}>{reason.message}</Typography>
          </Grid>
        ) : null })()}

        <Grid
          item
          xs={6}
        >
          <Button
            variant="outlined"
            color="warning"
            onClick={handleSkipInspection}
            disabled={isSaving}
            sx={{ width: "100%", mr: 1 }}
          >
            Skip Inspection
          </Button>
        </Grid>
        <Grid
          item
          xs={6}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving || !!getBlockReason()}
            sx={{ width: "100%" }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default InspectionLogForm;
